<template>
  <h1 v-if="organisation">{{ organisation.name }}</h1>
  <p v-else>Loading the organisation...</p>

  <div v-if="organisation">

    <div v-if="organisation.events.length === 0">
      <div class="alert alert-warning" role="alert">No events linked to the organisation yet</div>
    </div>

    <!-- Show the events linked to the organisation -->
    <div v-if="organisation.events.length > 0" class="d-flex align-items-center justify-content-between">
      <h2>Available shifts</h2>
      <div class="form-check form-switch">
        <input class="form-check-input" type="checkbox" role="switch" id="switchTeamLeadAdmin"
               v-model="teamLeadAdminEnabled">
        <label class="form-check-label" for="switchTeamLeadAdmin">Edit team leads</label>
      </div>
    </div>


    <div class="accordion accordion-flush" id="accordionOrganisationEvents">
      <div class="accordion-item" v-for="event in organisation.events" :key="event.code">
        <h2 class="accordion-header" :id="'heading' + event.code">
          <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                  :data-bs-target="'#flush-collapse' + event.code" aria-expanded="false"
                  :aria-controls="'flush-collapse' + event.code">
            {{ event.name }}
          </button>
        </h2>
        <div :id="'flush-collapse' + event.code" class="accordion-collapse collapse"
             :aria-labelledby="'heading' + event.code"
             data-bs-parent="#accordionOrganisationEvents">
          <div class="accordion-body">

            <!-- Show the locations linked to the event -->
            <div v-for="location in event.locations" :key="location.id">
              <h3 class="d-none d-sm-block">{{ event.name }} - {{ location.name }}</h3>
              <h3 class="d-block d-sm-none">{{ event.code }} - {{ location.name }}</h3>

              <!-- Show the shifts linked to the event on this location -->
              <div class="row row-cols-1 row-cols-md-3 g-4 mb-4">
                <div class="col" v-for="shift in location.shifts" :key="shift.id">

                  <!-- Show the shift details - in a card -->
                  <div class="card">
                    <div class="card-header">
                      <h4 class="card-title d-none d-md-block">{{ shift.name }}</h4>
                      <h4 class="card-title d-block d-md-none">{{ event.name }}<br>{{ location.name }}<br>{{
                          shift.name
                        }}</h4>
                    </div>
                    <div class="card-body">
                      <dl>
                        <dt v-if="shift.tag !== 'White'">Tag</dt>
                        <dd v-if="shift.tag !== 'White'">
                          <div class="color-tag"
                               :style="{ backgroundColor: shift.tag }"></div>
                          {{ shift.tag }}
                        </dd>
                        <dt>Free places</dt>
                        <dd>{{ shift.spots_left }}</dd>
                        <dt>From</dt>
                        <dd>{{ formatLocalTime(shift.start) }}</dd>
                        <dt>To</dt>
                        <dd>{{ formatLocalTime(shift.end) }}</dd>
                      </dl>
                      <div class="mb-3">
                        <!-- Request shift button -->
                        <button type="button" class="btn btn-primary w-100" v-if="!linkedShifts.includes(shift.id)"
                                :disabled="unavailableShifts.includes(shift.id)"
                                @click="requestShift(shift.id)">Request Shift
                        </button>
                        <!-- Cancel requested shift button -->
                        <button type="button" class="btn btn-secondary w-100" v-if="pendingShifts.includes(shift.id)"
                                @click="cancelShift(shift.id)">Cancel Requested Shift
                        </button>
                        <!-- Leave waiting list button -->
                        <button type="button" class="btn btn-warning w-100" v-if="waitingListShifts.includes(shift.id)"
                                @click="cancelShift(shift.id)">Leave waiting list
                        </button>
                        <!-- Confirmed shift - Disabled button -->
                        <button type="button" class="btn btn-success w-100" v-if="confirmedShifts.includes(shift.id)"
                                disabled>
                          Confirmed
                        </button>
                      </div>

                      <div>
                        <!-- Confirmed users-->
                        <ul class="compact-list">
                          <li v-for="userShift in shift.user_shifts_confirmed" :key="userShift.id">
                            <i class="fa-solid fa-square-check text-success"></i>&nbsp;
                            <template v-if="userIsAdmin">
                              <router-link
                                  :to="{ name: 'organisation-user', params: { slug: $route.params.slug, username: userShift.user.username } }">
                                {{ userShift.user.last_name }} {{ userShift.user.first_name }}
                              </router-link>
                              <button v-if="teamLeadAdminEnabled && !shift.team_leaders.includes(userShift.user.id)"
                                      type="button" class="btn btn-primary btn-sm m-1"
                                      @click="addAsTeamLead(shift.id,userShift.user.username)">Add as Team Lead
                              </button>
                              <button v-if="teamLeadAdminEnabled && shift.team_leaders.includes(userShift.user.id)"
                                      type="button" class="btn btn-warning btn-sm m-1"
                                      @click="removeTeamLead(shift.id,userShift.user.username)">Cancel Team Lead role
                              </button>
                            </template>
                            <template v-else>{{ userShift.user.last_name }} {{ userShift.user.first_name }}</template>
                          </li>
                        </ul>
                        <!-- Waiting list users -->
                        <ul class="compact-list">
                          <li v-for="userShift in shift.user_shifts_waiting_list" :key="userShift.id">
                            <i class="fa-solid fa-hourglass-half text-danger"></i>&nbsp;
                            <template v-if="userIsAdmin">
                              <router-link
                                  :to="{ name: 'organisation-user', params: { slug: $route.params.slug, username: userShift.user.username } }">
                                {{ userShift.user.last_name }} {{ userShift.user.first_name }}
                              </router-link>
                            </template>
                            <template v-else>{{ userShift.user.last_name }} {{ userShift.user.first_name }}</template>
                          </li>
                        </ul>
                        <!-- Pending users -->
                        <ul class="compact-list">
                          <li v-for="userShift in shift.user_shifts_pending" :key="userShift.id">
                            <i class="fa-solid fa-question text-warning"></i>&nbsp;
                            <template v-if="userIsAdmin">
                              <router-link
                                  :to="{ name: 'organisation-user', params: { slug: $route.params.slug, username: userShift.user.username } }">
                                {{ userShift.user.last_name }} {{ userShift.user.first_name }}
                              </router-link>
                            </template>
                            <template v-else>{{ userShift.user.last_name }} {{ userShift.user.first_name }}</template>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div v-if="shift.team_lead_users.length > 0" class="card-footer">
                      <span v-if="shift.team_lead_users.length > 0">
                        <strong>Team leaders:</strong>
                        <ul class="compact-list">
                          <li v-for="teamLeader in shift.team_lead_users" :key="teamLeader.id">
                            {{ teamLeader.last_name }} {{
                              teamLeader.first_name
                            }} {{ teamLeader.mobile_number_international }}
                          </li>
                        </ul>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Show the functions linked (via the timeslot) to the event -->
            <div v-for="mycrewFunction in event.functions" :key="mycrewFunction.code">
              <h3 class="d-none d-sm-block">{{ mycrewFunction.code }} {{ mycrewFunction.description }}</h3>
              <h3 class="d-block d-sm-none">{{ mycrewFunction.code }}</h3>

              <!-- Show the timeslots linked to the event -->
              <div v-for="timeslot in mycrewFunction.timeslots" :key="timeslot.id">
                <h4>{{ event.name }} <strong>{{ timeslot.name }}</strong></h4>

                <!-- Show the shifts linked to the timeslot -->
                <div class="row row-cols-1 row-cols-md-4 g-4 mb-4">
                  <div class="col" v-for="timeslotShift in timeslot.timeslot_shifts" :key="timeslotShift.id">
                    <div class="card">
                      <div class="card-body">
                        <h5 class="card-title">{{ event.code }} {{ mycrewFunction.code }} {{ timeslotShift.name }}</h5>
                        <dl>
                          <dt>Event</dt>
                          <dd>{{ event.name }}</dd>
                          <!--                          <dt>Function code</dt>-->
                          <!--                          <dd>{{ mycrewFunction.code }}</dd>-->
                          <dt>Description</dt>
                          <dd>{{ mycrewFunction.description }}</dd>
                          <dt>Location</dt>
                          <dd>{{ mycrewFunction.sub_location.location.name }}</dd>
                          <dt>Sublocation</dt>
                          <dd>{{ mycrewFunction.sub_location.name }}</dd>
                          <dt>From</dt>
                          <dd>{{ formatLocalTime(timeslotShift.start) }}</dd>
                          <dt>To</dt>
                          <dd>{{ formatLocalTime(timeslotShift.end) }}</dd>
                        </dl>
                        <div class="mb-3">
                          <!-- Request timeslotshift button -->
                          <button type="button" class="btn btn-primary w-100"
                                  @click="requestTimeslotShift(timeslotShift.id)">Request Shift
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Show the goodies linked to the organisation -->
    <h2 v-if="goodies && goodies.length > 0">Available goodies</h2>
    <table v-if="goodies && goodies.length > 0" class="table table-sm table-striped">
      <thead>
      <tr>
        <th>Name</th>
        <th>Description</th>
        <th>Status</th>
        <th>Action</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="goodie in goodies" :key="goodie.id">
        <!-- name -->
        <td>{{ goodie.name }}</td>
        <!-- description -->
        <td>{{ goodie.description }}</td>
        <!-- status -->
        <td>
          <span v-if="goodie.linked_user_goodie">{{ goodie.linked_user_goodie.status_display }}</span>
          <span v-else>Not requested</span>
        </td>
        <!-- action -->
        <td>
          <button v-if="!goodie.linked_user_goodie" class="btn btn-primary btn-sm" title="Request goodie"
                  @click="requestGoodie(goodie)">
            <i class="fa-solid fa-square-plus"></i>
          </button>
          <button v-if="goodie.linked_user_goodie"
                  class="btn btn-danger btn-sm" title="Cancel requested goodie"
                  :disabled="goodie.linked_user_goodie.status == 'confirmed'"
                  @click="confirmCancelRequestedGoodie(goodie.linked_user_goodie)">
            <i class="fa-solid fa-trash-can"></i>
          </button>
        </td>
      </tr>
      </tbody>
    </table>

  </div>
</template>

<script>
import {API_BASE_URL} from "@/config";
import dayjs from "dayjs";
import {cancelRequestedGoodie} from "@/utils/userGoodieUtils";

export default {
  name: 'OrganisationPage',
  props: {
    slug: {
      type: String,
      required: true,
    }
  },
  data() {
    return {
      organisation: null,
      linkedShifts: [],
      pendingShifts: [],
      waitingListShifts: [],
      confirmedShifts: [],
      unavailableShifts: [],
      userIsAdmin: false,
      goodies: null,
      userGoodies: null,
      teamLeadAdminEnabled: false, // Toggle for team lead admin functionality
    }
  },
  created() {
    this.fetchOrganisationEventInfo();
    this.fetchOrganisationGoodiesInfo();
    this.requestLinkedShifts();
    this.fetchCurrentUserIsAdminOfOrganisation();
    this.fetchUserGoodies();
  },
  methods: {
    getCsrfToken() {
      return document.cookie.split('; ').find(row => row.startsWith('csrftoken')).split('=')[1];
    },
    async fetchOrganisationEventInfo() {
      try {
        const response = await fetch(`${API_BASE_URL}/organisations/${this.$route.params.slug}/event-location-shifts`, {
          method: 'GET',
          credentials: 'include',
        });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          this.organisation = await response.json();
          document.title = `${this.organisation.name} - Shifts`;
        }
      } catch (error) {
        console.error('Error fetching organisation info:', error);
      }
    },
    async fetchOrganisationGoodiesInfo() {
      try {
        const response = await fetch(`${API_BASE_URL}/organisations/${this.$route.params.slug}/goodies/public`, {
          method: 'GET',
          credentials: 'include',
        });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          const data = await response.json();
          this.goodies = data;
        }
      } catch (error) {
        console.error('Error fetching organisation goodies info:', error);
      }
    },
    async requestGoodie(goodie) {
      try {
        const response = await fetch(`${API_BASE_URL}/goodies/${goodie.id}/user-goodies`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          credentials: 'include',
        });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await response.json();
          await this.fetchOrganisationGoodiesInfo();
        }
      } catch (error) {
        console.error('Error requesting goodie:', error);
      }

    },
    async fetchUserGoodies() {
      try {
        const response = await fetch(`${API_BASE_URL}/user-goodies`, {
          method: 'GET',
          credentials: 'include',
        });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          const data = await response.json();
          this.userGoodies = data;
        }
      } catch (error) {
        console.error('Error fetching user goodies:', error);
      }
    },
    async confirmCancelRequestedGoodie(userGoodie) {
      const confirmed = window.confirm(`Cancel the requested goodie "${userGoodie.goodie.name}?"`);
      if (confirmed) {
        await cancelRequestedGoodie(userGoodie.id, this.getCsrfToken());
        await this.fetchOrganisationGoodiesInfo();
        await this.fetchUserGoodies();
      }
    },
    async requestLinkedShifts() {
      try {
        // Retrieve a list of shifts that are linked to the current user
        const response = await fetch(`${API_BASE_URL}/current-user/shifts`,
            {
              method: 'GET',
              credentials: 'include',
            });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          const data = await response.json();
          this.linkedShifts = data.linked_shifts;
          this.pendingShifts = data.pending_shifts;
          this.waitingListShifts = data.waiting_list_shifts;
          this.confirmedShifts = data.confirmed_shifts;
          this.unavailableShifts = data.unavailable_shifts;
        }
      } catch (error) {
        console.error('Error fetching linked shifts:', error);
      }
    },
    async requestShift(shiftId) {
      try {
        // Create a user shift for the given shift
        const response = await fetch(`${API_BASE_URL}/shifts/${shiftId}/user-shifts`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': this.getCsrfToken(),
              },
              credentials: 'include',
            });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await response.json();
          await this.fetchOrganisationEventInfo();
          await this.requestLinkedShifts();
        }
      } catch (error) {
        console.error('Error requesting shift:', error);
      }
    },
    async cancelShift(shiftId) {
      try {
        const response = await fetch(`${API_BASE_URL}/shifts/${shiftId}/user-shifts`,
            {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': this.getCsrfToken(),
              },
              credentials: 'include',
            });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await response.json();
          await this.requestLinkedShifts();
          await this.fetchOrganisationEventInfo();
        }
      } catch (error) {
        console.error('Error cancelling shift:', error);
      }
    },
    formatLocalTime(dateString) {
      return dayjs(dateString).format('ddd DD/MM/YYYY HH:mm');
    },
    async fetchCurrentUserIsAdminOfOrganisation() {
      try {
        const response = await fetch(`${API_BASE_URL}/organisations/${this.$route.params.slug}/current-user/is-admin`, {
          method: 'GET',
          credentials: 'include',
        });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          const data = await response.json();
          this.userIsAdmin = data.status;
        }
      } catch (error) {
        console.error('Error fetching current user role:', error);
      }
    },


    async requestTimeslotShift(timeslotShiftId) {
      try {
        // Create a user shift for the given timeslot shift
        const response = await fetch(`${API_BASE_URL}/timeslot-shifts/${timeslotShiftId}/timeslot-user-shifts`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': this.getCsrfToken(),
              },
              credentials: 'include',
            });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await response.json();
          await this.fetchOrganisationEventInfo();
          await this.requestLinkedShifts();
        }
      } catch (error) {
        console.error('Error requesting shift:', error);
      }
    },
    async addAsTeamLead(shiftId, username) {
      try {
        // Add the user as a team lead for the given shift
        const response = await fetch(`${API_BASE_URL}/shifts/${shiftId}/team-leaders`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          credentials: 'include',
          body: JSON.stringify({username}),
        });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else if (response.status !== 201) {
          console.error('Failed to add team lead:', response.statusText);
        } else {
          await response.json();
          await this.fetchOrganisationEventInfo();
        }
      } catch
          (error) {
        console.error('Error adding team lead:', error);
      }
    },
    async removeTeamLead(shiftId, username) {
      try {
        // Remove the user as a team lead for the given shift
        const response = await fetch(`${API_BASE_URL}/shifts/${shiftId}/team-leaders`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          credentials: 'include',
          body: JSON.stringify({username}),
        });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else if (response.status !== 200) {
          console.error('Failed to remove team lead:', response.statusText);
        } else {
          await response.json();
          await this.fetchOrganisationEventInfo();
        }
      } catch
          (error) {
        console.error('Error removing team lead:', error);
      }
    },
  },
};
</script>

<style scoped>
.compact-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.color-tag {
  display: inline-block;
  width: 20px; /* Adjust size as needed */
  height: 20px; /* Adjust size as needed */
  margin-right: 5px; /* Adjust spacing between square and text */
  border: 1px solid black; /* Add black border */
  border-radius: 3px; /* Adjust border radius for rounded corners */
}
</style>